"use client";

import * as React from "react";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import {
	isShown,
	MenuItems,
	RowButton,
	RowMenu,
	useAsyncSelect,
	withDisabledReason,
	type ActionDescriptorBase,
	type DescribedButtonAction,
	type DescribedMenuItem,
	type SelectHandler,
} from "./action-descriptors";
import { ActionsMenu } from "./actions-menu";
import { CreateButton } from "./create-button";

/* -------------------------------------------------------------------------------------------------
 * Action descriptors
 *
 * A list-page or tab toolbar has no filled button, so there is no `primary` slot: the rule is true
 * by type. As in the record header, a descriptor carries what an action *is*, never how it looks —
 * there is no `variant`, `size` or `className` to reach for.
 * -----------------------------------------------------------------------------------------------*/

/** An item inside the `Download ▾` or `+ Add` menu. */
type ToolbarMenuItem = DescribedMenuItem;

/** A job launcher or utility: Import, Recalculate, Sync. Grey outline, icon required. */
type ToolbarButtonAction = DescribedButtonAction;

/** `+ Label` that runs a handler — typically opens a create sheet. */
type ToolbarCreateSelectAction = ActionDescriptorBase & {
	onSelect: SelectHandler;
	href?: never;
	items?: never;
};

/** `+ Label` that navigates, rendered through the toolbar's `linkAs`. */
type ToolbarCreateLinkAction = ActionDescriptorBase & {
	href: string;
	onSelect?: never;
	items?: never;
};

/**
 * `+ Add`, a menu for a toolbar that can create more than one kind of thing. Keeps its menu even with
 * a single item, so the affordance does not change shape as options come and go. Name what the menu
 * adds ("Add investing entity") when the tab does not already make it plain.
 */
type ToolbarCreateMenuAction = Omit<ActionDescriptorBase, "label"> & {
	/** Defaults to "Add". */
	label?: string;
	items: ToolbarMenuItem[];
	onSelect?: never;
	href?: never;
};

/** The create affordance. The caller chooses the verb — "Create" or "Add". */
type ToolbarCreateAction =
	| ToolbarCreateSelectAction
	| ToolbarCreateLinkAction
	| ToolbarCreateMenuAction;

/**
 * The component a `href` create renders through. It receives `href`, `className`, `children`, a
 * ref and the button's ARIA and data attributes, and must forward them to its anchor — the way
 * Next's `Link` does.
 */
type ToolbarLinkComponent = React.ElementType<{
	href: string;
	className?: string;
	children?: React.ReactNode;
}>;

type ToolbarActionsProps = {
	/** Job launchers and utilities, rendered left to right in array order. */
	actions?: ToolbarButtonAction[];
	/** Exports. One visible item renders as a button with a download icon; two or more as `Download ▾`. */
	download?: ToolbarMenuItem[];
	/** `{ onSelect }` or `{ href }` renders `+ Label`; `{ items }` renders a `+ Add` menu. Always last. */
	create?: ToolbarCreateAction;
	/**
	 * Renders a `href` create. Pass the app's router link (admin-app's `OrgLink`), so navigation
	 * stays client-side and org-scoped. Defaults to a plain anchor.
	 */
	linkAs?: ToolbarLinkComponent;
};

/** Title case keeps these lowercase, so they say nothing about a label's case. */
const MINOR_WORDS = new Set([
	"a",
	"an",
	"and",
	"as",
	"at",
	"by",
	"for",
	"from",
	"in",
	"of",
	"on",
	"or",
	"the",
	"to",
	"with",
]);

/**
 * True when every word after the verb is capitalised and at least one is a capitalised word rather
 * than an acronym: "Create Capital Raise" is title case, "Create KYC check" and "Download CSV" are
 * not.
 */
function isTitleCase(label: string): boolean {
	const words = label
		.trim()
		.split(/\s+/)
		.slice(1)
		.map((word) => word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))
		.filter(
			(word) => /^\p{L}/u.test(word) && !MINOR_WORDS.has(word.toLowerCase()),
		);

	return (
		words.length > 0 &&
		words.every((word) => /^\p{Lu}/u.test(word)) &&
		words.some((word) => /^\p{Lu}\p{Ll}+$/u.test(word))
	);
}

function warnOnTitleCase(labels: string[]): void {
	if (process.env.NODE_ENV === "production") {
		return;
	}
	for (const label of labels) {
		if (isTitleCase(label)) {
			// eslint-disable-next-line no-console -- no logger context in packages/ui
			console.warn(
				`ToolbarActions: "${label}" looks like title case. ` +
					"Toolbar labels are sentence case — capitalise only the first word, acronyms and proper nouns.",
			);
		}
	}
}

function CreateSelectButton({ action }: { action: ToolbarCreateSelectAction }) {
	const { pending, run } = useAsyncSelect(action.onSelect);

	return withDisabledReason(
		<CreateButton
			loading={pending}
			disabled={action.disabled}
			onClick={run}
			aria-label={action["aria-label"]}
			data-testid={action["data-testid"]}
		>
			{action.label}
		</CreateButton>,
		action,
	);
}

function CreateLinkButton({
	action,
	linkAs: LinkComponent,
}: {
	action: ToolbarCreateLinkAction;
	linkAs: ToolbarLinkComponent;
}) {
	// A disabled link stays focusable and still follows on Enter, so it renders as a button instead.
	if (action.disabled) {
		return withDisabledReason(
			<CreateButton
				disabled
				aria-label={action["aria-label"]}
				data-testid={action["data-testid"]}
			>
				{action.label}
			</CreateButton>,
			action,
		);
	}

	return (
		<CreateButton
			asChild
			aria-label={action["aria-label"]}
			data-testid={action["data-testid"]}
		>
			<LinkComponent href={action.href}>{action.label}</LinkComponent>
		</CreateButton>
	);
}

function CreateMenu({ action }: { action: ToolbarCreateMenuAction }) {
	const visibleItems = action.items.filter(isShown);

	if (visibleItems.length === 0) {
		return null;
	}

	return withDisabledReason(
		<ActionsMenu>
			<ActionsMenu.Trigger asChild>
				<CreateButton
					disabled={action.disabled}
					aria-label={action["aria-label"]}
					data-testid={action["data-testid"]}
				>
					{action.label ?? "Add"}
				</CreateButton>
			</ActionsMenu.Trigger>
			<ActionsMenu.Content>
				<MenuItems items={visibleItems} />
			</ActionsMenu.Content>
		</ActionsMenu>,
		action,
	);
}

function ToolbarCreate({
	action,
	linkAs,
}: {
	action: ToolbarCreateAction;
	linkAs: ToolbarLinkComponent;
}) {
	if (action.items) {
		return <CreateMenu action={action} />;
	}
	if (action.href !== undefined) {
		return <CreateLinkButton action={action} linkAs={linkAs} />;
	}
	return <CreateSelectButton action={action} />;
}

/**
 * The described action row for a list-page or tab toolbar. Renders `actions` in array order, then
 * Download, then Create — always 8px apart, whatever order the props are written in.
 *
 * It does not fold: inside `TablePage.Actions` the row already moves into the mobile Filter drawer.
 */
function ToolbarActions({
	actions = [],
	download = [],
	create,
	linkAs = "a",
}: ToolbarActionsProps) {
	const visibleActions = actions.filter(isShown);
	const visibleDownloads = download.filter(isShown);
	const visibleCreate = create && isShown(create) ? create : undefined;
	const visibleCreateItems = visibleCreate?.items?.filter(isShown) ?? [];

	warnOnTitleCase([
		...visibleActions.map((action) => action.label),
		...visibleDownloads.map((item) => item.label),
		...(visibleCreate?.label ? [visibleCreate.label] : []),
		...visibleCreateItems.map((item) => item.label),
	]);

	const hasCreate =
		visibleCreate !== undefined &&
		(!visibleCreate.items || visibleCreateItems.length > 0);

	if (
		visibleActions.length === 0 &&
		visibleDownloads.length === 0 &&
		!hasCreate
	) {
		return null;
	}

	return (
		<div data-slot="toolbar-actions" className="flex items-center gap-2">
			{withOccurrenceKeys(
				visibleActions,
				(action) => action["data-testid"] ?? action.label,
			).map(({ item: action, key }) => (
				<RowButton key={key} action={action} />
			))}
			{visibleDownloads.length > 0 && (
				<RowMenu
					action={{
						type: "download",
						label: "Download",
						items: visibleDownloads,
					}}
				/>
			)}
			{visibleCreate && (
				<ToolbarCreate action={visibleCreate} linkAs={linkAs} />
			)}
		</div>
	);
}

export { ToolbarActions };

export type {
	ToolbarActionsProps,
	ToolbarButtonAction,
	ToolbarCreateAction,
	ToolbarCreateLinkAction,
	ToolbarCreateMenuAction,
	ToolbarCreateSelectAction,
	ToolbarLinkComponent,
	ToolbarMenuItem,
};
